import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes, css } from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import TypingAnimation from 'components/Common/TypingAnimation'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useGlobalSearchVerticalTabs from 'hooks/GlobalSearch/useGlobalSearchVerticalTabs'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useCurrentGlobalSearchVerticalPage from 'hooks/GlobalSearch/useCurrentGlobalSearchVerticalPage'

const rotateGradient = keyframes`
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`

const SearchBar = styled.div<{ $showAIGlowBorder?: boolean }>`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(6)};
  padding-inline-start: ${rem(12)};
  border-radius: ${props => props.$showAIGlowBorder ? rem(2) : props.theme.borderRadius.S};
  box-shadow: ${props => props.theme.shadow.flat.small};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${props => props.$showAIGlowBorder ? 'none' : `1px solid ${props.theme.palette.neutral.surface.lightForeground}`};
  overflow: hidden;
  white-space: nowrap;
  margin: ${rem(16)} 0;

  ${props => props.$showAIGlowBorder && css`
    position: relative;
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: max(100vw, 100%);
      aspect-ratio: 1;
      background: conic-gradient(
        from 0deg,
        ${props.theme.palette.product.ai.light} 0deg,
        ${props.theme.palette.product.ai.dark} 180deg,
        ${props.theme.palette.product.ai.light} 360deg
      );
      animation: ${rotateGradient} 2500ms linear infinite;
      z-index: 0;
    }

    &::after {
      content: '';
      position: absolute;
      inset: ${rem(2)};
      background: ${props.theme.palette.neutral.surface.normalBackground};
      border-radius: ${rem(2)};
      z-index: 1;
    }

    > * {
      position: relative;
      z-index: 2;
    }
  `}

  ${mediaQueryUp.tablet} {
    display: none;
  }

  & > * + * {
    /* Inline-end margin because it's row-reverse */
    margin-inline-end: ${rem(4)};
  }
`

interface Props {
  placeholder?: string
  placeholderTextList?: Array<string>
  showAIGlowBorder?: boolean
}

function GlobalMobileSearchHeroBar(props: Props) {
  const { placeholder, placeholderTextList, showAIGlowBorder } = props
  const intl = useIntl()
  const globalSearchVerticalTabs = useGlobalSearchVerticalTabs()
  const currentSearchVerticalPage = useCurrentGlobalSearchVerticalPage()
  const { openedSearchVertical, toggleSearchVertical } = useGlobalSearchURLHashVertical()

  const toggleSearch = useCallback(() => {
    if (openedSearchVertical) {
      toggleSearchVertical('none')
    } else {
      if (!currentSearchVerticalPage) {
        toggleSearchVertical([...globalSearchVerticalTabs.keys()][0])
      } else {
        toggleSearchVertical(currentSearchVerticalPage)
      }
    }
  }, [currentSearchVerticalPage, globalSearchVerticalTabs, openedSearchVertical, toggleSearchVertical])
  const textList = useMemo(() => [...globalSearchVerticalTabs.values()].map(tab => tab.label),
    [globalSearchVerticalTabs])

  const searchPlaceholderText = useMemo(() => {
    if (placeholder) {
      return placeholder
    }
    return intl.formatMessage({
      id: 'aUOkFaobejWjrAJAuX1R',
      defaultMessage: 'Search great offers for',
      description: 'Placeholder text for global mobile search bar in the search interface',
    })
  }, [intl, placeholder])

  return (
    <SearchBar role="presentation" onClick={toggleSearch} $showAIGlowBorder={showAIGlowBorder}>
      <IconButton kind="primary" size="small"><LineSearchIcon /></IconButton>
      <BodyText variant="small" colour="light-foreground-alt">
        {searchPlaceholderText} &nbsp;
        <BodyText as="span" colour="normal-contrast" variant="small" weight="bold">
          <TypingAnimation textList={placeholderTextList || textList} disable={textList.length === 1} />
        </BodyText>
      </BodyText>
    </SearchBar>
  )
}

export default GlobalMobileSearchHeroBar
